import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, TrendingUp, Brain, Target, Users, BarChart3, ExternalLink } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const PivotMintDetail = () => {
  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh' }}>
      <Header />
      <main style={{ paddingTop: '180px' }}>
        {/* Hero Section */}
        <section style={{
          background: 'var(--bg-primary)',
          padding: '80px 7.6923%',
          position: 'relative'
        }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <Link to="/" style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--brand-primary)',
              textDecoration: 'none',
              fontSize: '16px',
              marginBottom: '30px',
              transition: 'all 0.3s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.transform = 'translateX(-5px)'}
            onMouseLeave={(e) => e.currentTarget.style.transform = 'translateX(0)'}
            >
              <ArrowLeft size={20} />
              Back to Solutions
            </Link>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '60px', alignItems: 'center' }} className="detail-grid">
              <div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_biz-innovations/artifacts/p7vyj2z6_Pivot%20mint%20blck%20Logo.jpeg" 
                  alt="PivotMint" 
                  style={{ height: '144px', objectFit: 'contain', marginBottom: '30px' }}
                />
                <h1 className="display-large" style={{ marginBottom: '20px' }}>
                  Discover Your Path, <span style={{ color: 'var(--brand-primary)' }}>Ignite Your Future</span>
                </h1>
                <p className="body-large" style={{ marginBottom: '30px' }}>
                  A Smart Career Navigator for Students in Grades 9 & 10
                </p>
                <a 
                  href="https://pivotmintcareer.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-primary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '12px' }}
                >
                  Visit Website
                  <ExternalLink size={20} />
                </a>
              </div>
              <div>
                <img 
                  src="https://images.unsplash.com/photo-1606857521015-7f9fcf423740" 
                  alt="Career Discovery" 
                  style={{ width: '100%', height: '400px', objectFit: 'cover' }}
                />
              </div>
            </div>
          </div>
        </section>

        {/* Overview */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{
              padding: '60px',
              background: 'rgba(0, 255, 209, 0.05)',
              border: '1px solid var(--brand-primary)'
            }}>
              <p className="body-large" style={{ lineHeight: '1.8' }}>
                In today's fast-changing world, choosing the right career can be overwhelming. Many students face uncertainty, limited guidance, and pressure from societal expectations. <strong>Pivot Mint</strong> brings intelligent, data-backed career discovery to empower students to make informed and meaningful choices about their futures.
              </p>
            </div>
          </div>
        </section>

        {/* Why Career Guidance Matters */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <h2 className="display-medium" style={{ marginBottom: '30px', textAlign: 'center' }}>
              Why <span style={{ color: 'var(--brand-primary)' }}>Career Guidance</span> Matters
            </h2>
            <p className="body-medium" style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto 60px' }}>
              Career counselling helps individuals align their strengths, passions, and aptitudes with the right professional paths. Without it, students often end up pursuing degrees disconnected from their true abilities, leading to underemployment and wasted potential.
            </p>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: '30px'
            }}>
              {[
                { icon: <Target size={40} />, text: 'Alignment between talent and opportunity' },
                { icon: <TrendingUp size={40} />, text: 'Reduction in skill gaps and job mismatches' },
                { icon: <Users size={40} />, text: 'Equal access to informed career choices for both urban and rural students' },
                { icon: <BarChart3 size={40} />, text: 'A more future-ready, productive workforce' }
              ].map((item, index) => (
                <div key={index} style={{
                  padding: '40px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid var(--border-subtle)',
                  transition: 'all 0.4s ease-in-out'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--brand-primary)';
                  e.currentTarget.style.transform = 'translateY(-5px)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border-subtle)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }}
                >
                  <div style={{ color: 'var(--brand-primary)', marginBottom: '20px' }}>
                    {item.icon}
                  </div>
                  <p className="body-medium">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* The Solution */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <h2 className="display-medium" style={{ marginBottom: '30px', textAlign: 'center' }}>
              The <span style={{ color: 'var(--brand-primary)' }}>Pivot Mint</span> Solution
            </h2>
            <p className="body-large" style={{ textAlign: 'center', maxWidth: '900px', margin: '0 auto' }}>
              Pivot Mint is a next-generation AI-powered career counselling application that blends psychometric assessments, market insights, and personalized guidance to help every learner find their best-fit career.
            </p>
            <p className="body-medium" style={{ textAlign: 'center', maxWidth: '900px', margin: '30px auto 0', color: 'var(--text-secondary)' }}>
              It serves as an interactive career companion, guiding students through every stage — from exploration to decision-making — with recommendations that are relevant, practical, and future-ready.
            </p>
          </div>
        </section>

        {/* Core Features */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <h2 className="display-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
              Core <span style={{ color: 'var(--brand-primary)' }}>Features</span>
            </h2>

            <div style={{ display: 'grid', gap: '30px' }}>
              {[
                {
                  title: 'Personalized Career Pathways',
                  description: 'AI-driven matching of interests, skills, and personality to ideal careers, courses, and industries.'
                },
                {
                  title: 'Skill Mapping & Gap Analysis',
                  description: 'Identifies user strengths and areas for upskilling with progress tracking.'
                },
                {
                  title: 'Market Trends & Job Data',
                  description: 'Provides real-time insights into roles, salaries, and industry demand.'
                },
                {
                  title: 'Career Path Visualizations',
                  description: 'Interactive dashboards show growth opportunities and career trajectories.'
                },
                {
                  title: 'Psychometric & Aptitude Assessments',
                  description: 'Built-in tests to help users discover their best-fit domains.'
                },
                {
                  title: 'Tailored for Grades 9 & 10',
                  description: 'Curated guidance designed to match academic level and aspirations.'
                },
                {
                  title: 'Easy Onboarding',
                  description: 'Adaptive sign-up process for instant personalization.'
                }
              ].map((feature, index) => (
                <div key={index} style={{
                  padding: '30px',
                  background: 'rgba(255, 255, 255, 0.03)',
                  border: '1px solid var(--border-subtle)',
                  display: 'flex',
                  gap: '20px',
                  transition: 'all 0.3s'
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--brand-primary)'}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--border-subtle)'}
                >
                  <CheckCircle2 size={24} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: '4px' }} />
                  <div>
                    <h3 className="heading-3" style={{ marginBottom: '8px' }}>{feature.title}</h3>
                    <p className="body-small">{feature.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* White Label Solution */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{
              padding: '60px',
              background: 'rgba(255, 255, 255, 0.03)',
              border: '1px solid var(--border-subtle)'
            }}>
              <h2 className="heading-1" style={{ marginBottom: '20px' }}>
                <Brain size={40} color="var(--brand-primary)" style={{ display: 'inline-block', marginRight: '15px', verticalAlign: 'middle' }} />
                White Label Solution for Institutions
              </h2>
              <p className="body-medium" style={{ lineHeight: '1.8' }}>
                Pivot Mint can be white-labelled for schools and edtech platforms, allowing institutions to offer fully customized career guidance under their own brand identity. Its modular enterprise version enables large-scale deployment, integrated learning analytics, and real-time performance monitoring for educators and counselors.
              </p>
            </div>
          </div>
        </section>

        {/* Why Choose */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <h2 className="display-medium" style={{ marginBottom: '60px', textAlign: 'center' }}>
              Why Choose <span style={{ color: 'var(--brand-primary)' }}>Pivot Mint</span>
            </h2>

            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '30px'
            }}>
              {[
                'Empowers students with clarity and confidence in career decisions',
                'Supports institutions with scalable, intelligent guidance tools',
                'Bridges the gap between education and employment market realities',
                'Offers future-proof career recommendations',
                'Delivers a rich, engaging, and modern experience using Agentic AI'
              ].map((benefit, index) => (
                <div key={index} style={{
                  padding: '30px',
                  background: 'rgba(0, 255, 209, 0.05)',
                  border: '1px solid var(--brand-primary)',
                  display: 'flex',
                  gap: '15px',
                  alignItems: 'flex-start'
                }}>
                  <CheckCircle2 size={24} color="var(--brand-primary)" style={{ flexShrink: 0, marginTop: '2px' }} />
                  <p className="body-medium">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section style={{ background: 'var(--bg-primary)', padding: '80px 7.6923%' }}>
          <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
            <div style={{
              padding: '80px 60px',
              background: 'rgba(0, 255, 209, 0.05)',
              border: '2px solid var(--brand-primary)',
              textAlign: 'center'
            }}>
              <h2 className="display-medium" style={{ marginBottom: '20px' }}>
                Ready to Transform <span style={{ color: 'var(--brand-primary)' }}>Career Guidance?</span>
              </h2>
              <p className="body-large" style={{ marginBottom: '40px', maxWidth: '800px', margin: '0 auto 40px' }}>
                Visit pivotmintcareer.com to schedule a demo, partner with Pivot Mint for your institution, or begin your personal career discovery journey today.
              </p>
              <div style={{ display: 'flex', gap: '20px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a 
                  href="https://pivotmintcareer.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="btn-primary"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '12px' }}
                >
                  Visit Website
                  <ExternalLink size={20} />
                </a>
                <Link to="/#contact" className="btn-secondary">
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      <style>{`
        @media (max-width: 768px) {
          .detail-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
};

export default PivotMintDetail;
